/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-17
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

// Add local dependencies.
import {
  useGetMasterItemsQuery,
  useDeleteMasterItemMutation,
} from '../../../api/classEssentialsApi';
import MasterItemDialog from './MasterItemDialog';
import ConfirmDialog from './ConfirmDialog';
import type { MasterItem } from '../../../types/classEssentials';

/**
 * Props for MasterItemsManagement component
 */
interface MasterItemsManagementProps {
  studioId: string;
}

/**
 * Master Items Management component.
 * CRUD interface for managing studio's master catalog of class items.
 *
 * @param props - Component props
 * @returns {React.ReactElement} The master items management component
 */
const MasterItemsManagement: React.FC<MasterItemsManagementProps> = ({
  studioId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MasterItem | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const {
    data: items = [],
    isLoading,
    error,
  } = useGetMasterItemsQuery(studioId);
  const [deleteItem] = useDeleteMasterItemMutation();

  const handleAddItem = () => {
    setEditingItem(null);
    setIsDialogOpen(true);
  };

  const handleEditItem = (item: MasterItem) => {
    setEditingItem(item);
    setIsDialogOpen(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      await deleteItem(deleteItemId).unwrap();
      setSnackbarMessage('Item deleted successfully');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Failed to delete item');
      setSnackbarOpen(true);
    }
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingItem(null);
  };

  // Filter items based on search query
  const filteredItems = items.filter(
    item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load master items. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header and Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Master Item Catalog
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Add Item
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search items..."
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Items Table */}
      {filteredItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery
              ? 'No items match your search.'
              : 'No items yet. Add your first item to get started.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell>Category</TableCell>}
                {!isMobile && <TableCell>Description</TableCell>}
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map(item => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.name}
                    </Typography>
                    {isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {item.category}
                      </Typography>
                    )}
                  </TableCell>
                  {!isMobile && <TableCell>{item.category}</TableCell>}
                  {!isMobile && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.description}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {item.price ? `$${Number(item.price).toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.is_available ? 'Available' : 'Unavailable'}
                      color={item.is_available ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEditItem(item)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteItem(item.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Item Dialog */}
      <MasterItemDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        studioId={studioId}
        item={editingItem}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setDeleteItemId(null);
        }}
        severity="error"
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default MasterItemsManagement;
